import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { headingStyles, paragraphSmall } from '@helpers/typography';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import SkipLinkStyles from '@stories/Components/General/SkipLink/SkipLink.styles';
import styled, { css } from 'styled-components';
const Container = styled.section `
  --bgColour: ${brand.primary.purple};
  --fgColour: ${brand.primary.offWhite};

  position: relative;
  display: flex;
  min-height: 100vh;
  background-color: var(--bgColour);

  ${SkipLinkStyles.Container} {
    position: absolute;
    top: var(--headerHeight, 0px);
    left: 50%;
    transform: translateX(-50%);
  }

  .equally-ai___high-saturation & {
    filter: saturate(200%);
  }

  .equally-ai___reverse-contrast & {
    filter: invert(100%);
  }

  .equally-ai___monochrome-contrast & {
    filter: grayscale(100%);
  }

.equally-ai___reverse-contrast.equally-ai___monochrome-contrast & {
  filter: grayscale(100%) invert(100%);
}
`;
const Inner = styled.div `
  ${siteWide};
  ${baseGrid};

  grid-template-rows: 1.15fr 1fr;

  @media ${from(Device.TabletLarge)} {
    grid-template-rows: auto 1fr;
    row-gap: ${fluid(20, 40)};
  }
`;
const Image = styled.img `
  ${transition('opacity', 1)};
  ${imageCoverContainer()};

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
`;
const ContentWrapper = styled.div `
  ${transition('opacity, transform')};

  grid-column: 1 / span 12;
  grid-row: 1 / span 1;

  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-direction: column;
  padding: var(--headerHeight, 0px) 0 0;
  position: relative;
  z-index: 1;

  &[data-active='false'] {
    pointer-events: none;
    opacity: 0;
    transform: translateY(15%);

    ${ButtonStyles.Button} {
      opacity: 0;
      transform: translateX(-15%);
    }
  }

  ${ButtonStyles.Button} {
    ${transition('opacity, transform')};

    order: 3;
    transition-delay: 600ms;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 7 / span 6;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 7 / span 5;
  }
`;
const titleStyles = css `
  ${headingStyles};

  font-size: ${fluid(40, 86)};
  line-height: ${fluid(46, 98)};
  color: var(--fgColour);
`;
const Title = styled.h1 `
  ${transition('opacity, transform')};
  ${titleStyles};

  order: 2;
  transition-delay: 300ms;

  ${ContentWrapper}[data-active='false'] & {
    opacity: 0;
    transform: translateX(-15%);
  }
`;
const Subtitle = styled.span `
  ${transition('background-size', 1)};
  ${titleStyles};

  order: 1;
  color: transparent;
  background: transparent;
  background-image: linear-gradient(to right, var(--highlightColour), var(--highlightColour));
  background-repeat: no-repeat;
  background-size: 100% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  transition-delay: 0.05s;

  ${ContentWrapper}[data-animate='false'] & {
    background-size: 0% 100%;
  }

  @media ${from(Device.TabletLarge)} {
    transition-delay: 550ms;
    transition-duration: 1.5s;
  }

  @media ${from(Device.Desktop)} {
    transition-delay: 0.75s;
    transition-duration: 2.3s;
  }
`;
const TabsWrapper = styled.div `
  grid-column: 1 / span 12;
  grid-row: 2 / span 1;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: ${fluid(14, 28)};
  padding: ${fluid(10, 20)} 0 ${fluid(30, 50)};

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
  }
`;
const TabButton = styled.button `
  ${ButtonReset};
  ${paragraphSmall};
  ${transition('color')};

  position: relative;
  text-decoration: none;
  padding: ${fluid(8, 12)} 0 ${fluid(4, 6)};
  display: inline-flex;
  align-items: flex-start;
  color: var(--fgColour);
  text-transform: uppercase;
  margin: 0;

  &::after {
    ${transition('background-size')};

    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 100%;
    background: transparent;
    background-image: linear-gradient(to right, var(--fgColour), var(--fgColour));
    background-repeat: no-repeat;
    background-size: 0% 100%;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &:not([data-active='true']) {
    &.focus-ring,
    &:hover {
      color: var(--highlightColour);
    }
  }

  &[data-active='true']::after {
    background-size: 100% 100%;
  }
`;
const FallbackIcon = styled.div `
  ${transition('opacity', 1)};

  position: absolute;
  left: 1%;
  bottom: 3%;
  width: 60%;
  overflow: hidden;

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;

    > svg {
      transform: none;
    }
  }

  > svg {
    ${transition('transform', 0.5)};

    display: block;
    width: 100%;
    color: var(--fgColour);
    transform: rotate(13deg);
    transition-delay: 200ms;

    path:last-of-type {
      stroke: var(--highlightColour);
    }
  }

  @media ${from(Device.TabletLarge)} {
    width: 37.5%;
  }
`;
const BeamWrapper = styled.div `
  ${transition('opacity', 0.5)};

  grid-column: 1 / span 12;
  grid-row: 1 / span 2;
  pointer-events: none;

  &[data-animate='false'] {
    opacity: 0;
    transition-duration: 0ms;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 2 / span 10;
  }
`;
const LightBeam = styled.div `
  ${transition('transform', 1)};

  height: 100%;
  width: 100%;
  background: transparent;
  background-image: linear-gradient(
    to right,
    transparent calc(100% - 54px),
    hsla(var(--highlightColour) / 0.33) calc(100% - 16px),
    hsla(var(--highlightColour) / 0.6) calc(100% - 1px),
    hsla(var(--highlightColour) / 0.8) 100%
  );
  background-repeat: no-repeat;
  background-size: 100% 100%;

  ${BeamWrapper}[data-animate='false'] & {
    transform: translateX(calc((100% - 4px) * -1));
  }

  @media ${until(Device.Desktop)} {
    width: calc(100% + (var(--sitePadding) * 2));
    margin-left: calc(var(--sitePadding) * -1);
  }

  @media ${from(Device.Desktop)} {
    transition-duration: 2.6s;
  }
`;
const HomepageHeroBannerStyles = {
    BeamWrapper,
    Container,
    ContentWrapper,
    FallbackIcon,
    Image,
    Inner,
    LightBeam,
    Subtitle,
    TabButton,
    TabsWrapper,
    Title,
};
export default HomepageHeroBannerStyles;
